package services.http

import it.neckar.datetime.minimal.LocalDate
import it.neckar.datetime.minimal.Month
import it.neckar.datetime.minimal.Year
import it.neckar.lizergy.model.financial.currency.Money
import it.neckar.lizergy.model.financial.currency.sum
import it.neckar.lizergy.model.invoice.InvoiceNumber
import it.neckar.lizergy.model.project.previews.AccountingResolvedProjectPreview
import it.neckar.open.unit.currency.EUR
import it.neckar.rest.RestResponse
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

@Serializable
sealed interface AccountingProjectQueryResponse : RestResponse<AccountingProjectQueryInformation> {

  @Serializable
  @SerialName("good")
  data class Good(override val data: AccountingProjectQueryInformation) : AccountingProjectQueryResponse, RestResponse.GoodResponse<AccountingProjectQueryInformation>

  @Serializable
  @SerialName("bad/Failed")
  data object Failed : AccountingProjectQueryResponse, RestResponse.BadResponse<AccountingProjectQueryInformation>

  companion object {
    /**
     * Helper method to create a success object - with the correct type
     */
    fun good(data: AccountingProjectQueryInformation): AccountingProjectQueryResponse = Good(data)

    /**
     * Helper method to create a failure object - with the correct type
     */
    fun bad(): AccountingProjectQueryResponse = Failed

    /**
     * Creates a success method for non-null results, failure for null results
     */
    fun createFor(data: AccountingProjectQueryInformation?): AccountingProjectQueryResponse {
      return if (data != null) {
        good(data)
      } else {
        bad()
      }
    }
  }
}

@Serializable
data class AccountingProjectQueryInformation(
  val visibleProjectPreviews: List<AccountingResolvedProjectPreview>,
  val numberOfTotalFilteredProjects: Int,
  val numberOfTotalRelevantProjects: Int,
  val lizergyRevenue: CompanyRevenue,
  val neckarITRevenue: CompanyRevenue,
  val partnerRevenues: List<CompanyRevenue>,
)

@Serializable
data class CompanyRevenue(
  val companyName: String,
  val revenues: List<RevenueInformation>,
) {
  val totalRevenue: @EUR Money
    get() = revenues.map { it.revenue }.sum()

  val revenueByInvoiceNumber: Map<InvoiceNumber?, @EUR Money>
    get() = revenues.groupBy({ it.invoiceNumber }) { it.revenue }.mapValues { it.value.sum() }

  val projectCountByInvoiceNumber: Map<InvoiceNumber?, Int>
    get() = revenues.groupBy({ it.invoiceNumber }) { it.revenue }.mapValues { it.value.size }

  val revenueByDay: Map<LocalDate?, @EUR Money>
    get() = revenues.groupBy({ it.date }) { it.revenue }.mapValues { it.value.sum() }

  val projectCountsByDay: Map<LocalDate?, Int>
    get() = revenues.groupBy({ it.date }) { it.revenue }.mapValues { it.value.size }

  val revenueByMonth: Map<Month?, @EUR Money>
    get() = revenues.groupBy({ it.date?.month }) { it.revenue }.mapValues { it.value.sum() }

  val projectCountsByMonth: Map<Month?, Int>
    get() = revenues.groupBy({ it.date?.month }) { it.revenue }.mapValues { it.value.size }

  val revenueByYear: Map<Year?, @EUR Money>
    get() = revenues.groupBy({ it.date?.year }) { it.revenue }.mapValues { it.value.sum() }

  val projectCountsByYear: Map<Year?, Int>
    get() = revenues.groupBy({ it.date?.year }) { it.revenue }.mapValues { it.value.size }
}

@Serializable
data class RevenueInformation(
  val revenue: @EUR Money,
  val date: LocalDate?,
  val invoiceNumber: InvoiceNumber?,
)
